import { Link } from 'react-router-dom';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { LogoutButton } from '@/components/auth/LogoutButton';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useSidebarStore, useThemeStore, useUIStore } from '@/store';
import { useUser } from '@/store/currentUserStore';

export const Header = () => {
  const { user: profile } = useUserProfile();
  const currentUser = useUser();
  const { toggleMobileMenu } = useSidebarStore();
  const { theme } = useThemeStore();
  const { isProfileDropdownOpen, toggleProfileDropdown, closeProfileDropdown } = useUIStore();

  const getInitials = (name?: string) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <header className="safe-top sticky top-0 z-50 h-16 border-b border-gray-200 bg-white dark:border-dark-700 dark:bg-dark-900">
      <div className="page-max-width flex h-full items-center justify-between px-3 sm:px-6">
        {/* Left side - Menu button */}
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Mobile menu button */}
          <button
            onClick={toggleMobileMenu}
            className="rounded-lg p-1.5 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-dark-800 sm:p-2 lg:hidden"
            aria-label="Toggle menu"
          >
            <svg
              className="h-5 w-5 sm:h-6 sm:w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>

          <Link to="/" className="inline-flex items-center justify-center lg:hidden">
            <img
              src={`/assets/layout/images/logo-${theme === 'dark' ? 'dark' : 'light'}.png`}
              alt="Tankers International Logo"
              className="h-8 w-auto max-w-[120px] border-0 object-contain sm:h-11 sm:max-w-[200px]"
            />
          </Link>
        </div>

        {/* Right side - Theme toggle and profile */}
        <div className="flex items-center gap-1 sm:gap-3">
          {/* Theme toggle */}
          <ThemeToggle />

          {/* User profile dropdown */}
          <div className="relative">
            <button
              onClick={toggleProfileDropdown}
              className="flex items-center gap-1.5 rounded-lg p-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-dark-800 sm:gap-3 sm:p-2"
            >
              {profile?.photo ? (
                <img
                  src={profile.photo}
                  alt={currentUser?.name || 'User'}
                  className="h-7 w-7 rounded-full sm:h-8 sm:w-8"
                />
              ) : (
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-primary-600 text-xs font-semibold text-white sm:h-8 sm:w-8 sm:text-sm">
                  {getInitials(currentUser?.name)}
                </div>
              )}
              <div className="hidden text-left md:block">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {currentUser?.name || 'User'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{currentUser?.email}</p>
              </div>
              <svg
                className={`hidden h-4 w-4 text-gray-400 transition-transform md:block ${isProfileDropdownOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {/* Dropdown menu */}
            {isProfileDropdownOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={closeProfileDropdown} />
                <div className="absolute right-0 z-20 mt-2 w-56 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-dark-700 dark:bg-dark-800">
                  <div className="border-b border-gray-200 p-3 dark:border-dark-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {currentUser?.name || 'User'}
                    </p>
                    <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                      {currentUser?.email}
                    </p>
                  </div>
                  <div className="py-2">
                    <Link
                      to="/user-profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-dark-700"
                      onClick={closeProfileDropdown}
                    >
                      Profile
                    </Link>
                  </div>
                  <div className="border-t border-gray-200 p-2 dark:border-dark-700">
                    <LogoutButton className="w-full rounded px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100 dark:text-red-400 dark:hover:bg-dark-700" />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
